import { useEffect, useState } from "react";
import { X, Newspaper } from "lucide-react";
import DigestSubscribeForm from "@/components/DigestSubscribeForm";
import { trackEvent } from "@/lib/ga4";

/**
 * Popup que aparece **30s** após o leitor abrir uma página de Notícias
 * Jurídicas, oferecendo a inscrição no digest semanal.
 *
 * Regras (anti-irritação):
 *  - Aparece UMA VEZ POR SESSÃO (localStorage flag "juristube:digest:dismissed_until").
 *  - Quem dispensar fica imune por 30 dias.
 *  - Quem se inscrever com sucesso (estado "sent"/"already") nunca mais
 *    é molestado (flag separada "juristube:digest:subscribed").
 *
 * Esses flags ficam no client — não há overhead no servidor.
 */
const SESSION_KEY = "juristube:digest:dismissed_until";
const SUBSCRIBED_KEY = "juristube:digest:subscribed";
const SHOW_DELAY_MS = 30000;
const DISMISS_DAYS = 30;

export default function DigestSubscribeModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Já assinou? Nunca mais incomoda.
    try {
      if (localStorage.getItem(SUBSCRIBED_KEY) === "1") return;
      const dismissedUntil = parseInt(localStorage.getItem(SESSION_KEY) || "0", 10);
      if (dismissedUntil && Date.now() < dismissedUntil) return;
    } catch (e) {
      // localStorage indisponível (private browsing). Segue a vida.
    }
    const t = setTimeout(() => {
      setOpen(true);
      trackEvent("digest_modal_show", { trigger: "auto-30s" });
    }, SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    try {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(SESSION_KEY, String(until));
    } catch {}
    trackEvent("digest_modal_dismiss", {});
  };

  // Observa o sucesso do form pra marcar como "subscribed" e fechar.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      // Detecta inscrição/já-inscrito olhando a aparição do bloco de sucesso.
      const success = document.querySelector('[data-testid="digest-modal-form-success"]');
      if (success) {
        try { localStorage.setItem(SUBSCRIBED_KEY, "1"); } catch {}
        setTimeout(() => setOpen(false), 4000);
      }
    };
    const obs = new MutationObserver(handler);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6 bg-slate-950/75 backdrop-blur-sm"
      data-testid="digest-modal-overlay"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-labelledby="digest-modal-title"
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-sky-500/30 bg-gradient-to-br from-slate-900 to-slate-950 shadow-2xl p-6 sm:p-7 animate-in fade-in slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="digest-modal"
      >
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition"
          aria-label="Fechar"
          data-testid="digest-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Newspaper className="w-4 h-4 text-sky-400" />
          <span id="digest-modal-title" className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sky-300/90">
            Boletim do JurisTube
          </span>
        </div>

        <h2 className="font-serif text-2xl text-white font-bold leading-tight">
          Receba automaticamente as novidades do site
        </h2>

        <p className="text-sm text-slate-400 leading-relaxed mt-2 mb-5">
          Mandamos um boletim com os <strong className="text-slate-200">posts do blog</strong>, os{" "}
          <strong className="text-slate-200">novos vídeos</strong>, as{" "}
          <strong className="text-slate-200">principais decisões</strong> dos tribunais superiores e as{" "}
          <strong className="text-slate-200">normas novas</strong> publicadas no DOU. 5 minutos de leitura,
          zero spam.
        </p>

        <DigestSubscribeForm
          variant="banner"
          source="legal-news-modal"
          testidPrefix="digest-modal-form"
        />

        <button
          type="button"
          onClick={close}
          className="mt-4 text-[11px] text-slate-500 hover:text-slate-300 underline transition"
          data-testid="digest-modal-skip"
        >
          Não, obrigado — voltarei direto ao site quando precisar.
        </button>
      </div>
    </div>
  );
}
